import { Stages, STAGE } from '../config'

export const trace = <T>(arg: T, ...rest): T => {
  const allowedStages = [Stages.local, Stages.ci, Stages.test]
  if (allowedStages.includes(STAGE)) {
    console.trace('[TRACE]', arg, ...rest)
  }
  return arg
}

export const distinctObjectsArray = (primaryKey: string) => <T>(
  arr: T[]
): T[] =>
  arr.reduce(
    ($: T[], obj: T): T[] =>
      $.find((item) => item[primaryKey] === obj[primaryKey]) ? $ : [...$, obj],
    []
  )

export const decorateFirst = <T>(callback) => ([first, ...rest]: T[]): T[] =>
  first ? [callback(first), ...rest] : []

export function copyToClipboard(str: any, callback: () => void) {
  // This is available in Chrome but isn't a standard feature.
  // See https://github.com/Microsoft/TypeScript/issues/26728
  // @ts-ignore
  navigator.clipboard.writeText(str).then(callback)
}

export const objectToArray = <T>(obj: { [key: number]: T }): T[] =>
  Object.assign([], obj)

export const callEach = <T>(...fns: Array<(arg: T) => void>) => (
  arg: T
): void => {
  fns.forEach((fn) => fn(arg))
}

export const someIsLoading = (
  args: Array<{ isLoading?: boolean; [key: string]: any }>
): boolean => !!args.some(({ isLoading }) => !!isLoading)

export const isEmptyObject = (maybeObject: any): boolean =>
  !!maybeObject &&
  maybeObject.constructor === Object &&
  Object.entries(maybeObject).length === 0

export const createArray = (length: number): undefined[] =>
  Array(Math.max(0, length) || 0).fill(undefined)

export const trimExtraSpaces = (value: string): string =>
  value.replace(/\s+/g, ' ').trim()

export const capitalizeFirstLetter = ([first, ...rest]: string): string =>
  `${first.toUpperCase()}${rest.join('')}`

export interface IAnyObject {
  [x: string]: any
}

export interface IStringObject {
  [x: string]: string
}

export type ObjectWith<K extends string, T = string> = Record<K, T> & IAnyObject

export const match = <K extends string, T = string>(
  unwrap: (obj: ObjectWith<K, T>) => T
) => (key) => (obj: ObjectWith<K, T>) => unwrap(obj) === key

export const unwrapId = ({ id }: ObjectWith<'id'>) => id

export const wrapId = (id: string) => ({ id })

export const matchId = match(unwrapId)

export const notMatch = <K extends string, T = string>(
  unwrap: (obj: ObjectWith<K, T>) => T
) => (key) => (obj: ObjectWith<K, T>) => unwrap(obj) !== key

export const notMatchId = notMatch(unwrapId)

type PredicateFunction<T = any> = (param: T) => boolean

export const decorateIf = <T extends IAnyObject>(
  predicate: PredicateFunction<T>
) => (additionals: IAnyObject) => (item: T) =>
  predicate(item) ? { ...item, ...additionals } : item

/**
 * Attempts to parse a string as JSON, if it's a valid JSON will return parsed object,
 * if an error is caught - the initial string will be returned
 *
 * @param text - string that can possibly be JSON
 *
 * @returns Either parsed JSON object or the initial string
 */
export const attemptJSONParse = (text: string): any => {
  try {
    return JSON.parse(text)
  } catch (err) {
    return text
  }
}

export const getKeyValueCaseInsensitive = <T>(
  object: T,
  caseSensitiveKey: string
): any => {
  const caseInsensitiveKey = Object.keys(object).find(
    (objKey) => objKey.toLowerCase() === caseSensitiveKey.toLowerCase()
  )
  return !!caseInsensitiveKey ? object[caseInsensitiveKey] : undefined
}
